import { Form, Head, router, usePage } from "@inertiajs/react"
import { LoaderCircle } from "lucide-react"
import { useState } from "react"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AuthLayout from "@/layouts/auth-layout"
import { newIdentityPasswordResetPath, signInPath, signUpPath } from "@/routes"
import type { DemoUser } from "@/types"

export default function Login() {
  const { demo_users } = usePage<{ demo_users?: DemoUser[] }>().props
  const [demoProcessing, setDemoProcessing] = useState(false)

  const signInAs = (email: string, password: string) => {
    router.post(
      signInPath(),
      { email, password },
      {
        onStart: () => setDemoProcessing(true),
        onFinish: () => setDemoProcessing(false),
      },
    )
  }

  return (
    <AuthLayout
      title="Log in to your account"
      description="Enter your email and password below to log in"
    >
      <Head title="Log in" />
      <Form
        method="post"
        action={signInPath()}
        resetOnSuccess={["password"]}
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoFocus
                  tabIndex={1}
                  autoComplete="email"
                  placeholder="email@example.com"
                />
                <InputError message={errors.email} />
              </div>

              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password</Label>
                  <TextLink
                    href={newIdentityPasswordResetPath()}
                    className="ml-auto text-sm"
                    tabIndex={5}
                  >
                    Forgot password?
                  </TextLink>
                </div>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  required
                  tabIndex={2}
                  autoComplete="current-password"
                  placeholder="Password"
                />
                <InputError message={errors.password} />
              </div>

              <Button
                type="submit"
                className="mt-4 w-full"
                tabIndex={4}
                disabled={processing}
              >
                {processing && (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                )}
                Log in
              </Button>
            </div>

            <div className="text-muted-foreground text-center text-sm">
              Don&apos;t have an account?{" "}
              <TextLink href={signUpPath()} tabIndex={5}>
                Sign up
              </TextLink>
            </div>
          </>
        )}
      </Form>

      {/* Dev/staging only — SessionsController#new omits `demo_users` in test/production. */}
      {demo_users && demo_users.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="bg-border h-px flex-1" />
            <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Demo accounts
            </span>
            <div className="bg-border h-px flex-1" />
          </div>
          <p className="text-muted-foreground text-center text-xs">
            Available in development and staging only. Click to sign in.
          </p>
          <div className="flex flex-col gap-2">
            {demo_users.map((u) => (
              <Button
                key={u.email}
                type="button"
                variant="outline"
                onClick={() => signInAs(u.email, u.password)}
                disabled={demoProcessing}
                className="h-auto flex-col items-start gap-1 py-3 text-left"
              >
                <span className="text-sm font-semibold">{u.name}</span>
                <span className="text-muted-foreground text-xs">{u.email}</span>
                <span className="text-muted-foreground text-xs">
                  {u.caption}
                </span>
              </Button>
            ))}
          </div>
        </div>
      )}
    </AuthLayout>
  )
}
